import React, { useCallback, useMemo } from "react";
import { useMutation } from "react-apollo";
import {
  GuestNewsletterSubscribeMutation,
  IGuestNewsletterSubscribeRes,
  IGuestNewsletterSubscribeVar,
} from "src/hoc/persister/guestNewsletterSubscribe/graphql/GuestNewsletterSubscribe";
import {
  IUserNewsletterSubscribeRes,
  IUserNewsletterSubscribeVar,
  UserNewsletterSubscribeMutation,
} from "src/hoc/persister/userNewsletterSubscribe/graphql/UserNewsletterSubscribe";
import { useMe } from "src/hooks";
import { emailValidation } from "src/services/formValidation.service";
import { translate } from "src/services/translation.service";
import theme, { media } from "src/theme";
import styled from "styled-components";
import { object } from "yup";

import { Button } from "..";
import SingleFieldForm from "../Form/SingleFieldForm";

const StyledButton = styled(Button)`
  margin-top: ${theme.margins.marginBase};
`;

const EmailForm = styled(SingleFieldForm)`
  margin-top: 20px;

  ${media.laptop} {
    order: 0;
  }
`;

interface IProps {
  onUserSubscribe?: () => void;
  onGuestSubscribe?: (res: IGuestNewsletterSubscribeRes) => void;
  className?: string;
  buttonLabel?: string;
}

export const NewsletterSubscribe = ({
  onUserSubscribe,
  onGuestSubscribe,
  buttonLabel = translate("footer.submit"),
  className,
}: IProps): JSX.Element => {
  const { me } = useMe();

  /**
   * Guest user subscribe
   */
  const [
    guestNewsletterSubscribe,
    { loading: guestNewsletterSubscribeLoading },
  ] = useMutation<IGuestNewsletterSubscribeRes, IGuestNewsletterSubscribeVar>(
    GuestNewsletterSubscribeMutation
  );

  const guestSubscribeToNewsletter = useCallback(
    async (email: string): Promise<void> => {
      const { errors, data } = await guestNewsletterSubscribe({
        variables: { input: { email, subscribedToNewsletter: true } },
      });

      if (!errors && data?.guestNewsletterSubscribe) {
        onGuestSubscribe?.(data);
      }
    },
    [guestNewsletterSubscribe, onGuestSubscribe]
  );

  /**
   * Logged user subscribe
   */
  const [
    userNewsletterSubscribe,
    { loading: userNewsletterSubscribeLoading },
  ] = useMutation<IUserNewsletterSubscribeRes, IUserNewsletterSubscribeVar>(
    UserNewsletterSubscribeMutation
  );

  const subscribeToNewsletter = useCallback(async (): Promise<void> => {
    const { errors, data } = await userNewsletterSubscribe({
      variables: { input: { subscribedToNewsletter: true } },
    });

    if (!errors && data?.userNewsletterSubscribe) {
      onUserSubscribe?.();
    }
  }, [userNewsletterSubscribe, onUserSubscribe]);

  /**
   * Yup schema for email,
   * only usefull when me is null
   */
  const schema = useMemo(
    () => (!me ? object().shape(emailValidation) : undefined),
    [me]
  );

  if (me) {
    const disabled =
      typeof me?.subscribedToNewsletter === "boolean"
        ? me.subscribedToNewsletter
        : false;

    const title = translate(
      disabled ? "footer.alreadySubscribed" : "footer.subscribe"
    );

    return (
      <StyledButton
        className={className}
        fullWidth
        title={title}
        onClick={subscribeToNewsletter}
        loading={userNewsletterSubscribeLoading}
        disabled={disabled}
      />
    );
  }

  return (
    <EmailForm
      className={className}
      buttonLabel={buttonLabel}
      fieldName="email"
      inputType="email"
      onButtonClick={guestSubscribeToNewsletter}
      validationSchema={schema}
      loading={guestNewsletterSubscribeLoading}
      placeholder="email@example.com"
    />
  );
};

NewsletterSubscribe.MonoButton = StyledButton;
NewsletterSubscribe.EmailForm = EmailForm;
